import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { TrashListResponse } from "@family/shared";

interface Props {
  familySlug: string;
}

const KIND_LABEL: Record<string, string> = {
  post: "Post",
  event: "Event",
  news: "News",
};

export function TrashAdmin({ familySlug }: Props) {
  const queryClient = useQueryClient();

  const trashQuery = useQuery<TrashListResponse>({
    queryKey: ["trash", familySlug],
    queryFn: () => apiFetch<TrashListResponse>(`/families/${familySlug}/trash`),
  });

  const restore = useMutation({
    mutationFn: ({ kind, id }: { kind: string; id: string }) =>
      apiFetch(`/families/${familySlug}/trash/${kind}/${id}/restore`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trash", familySlug] });
      // Invalidate the relevant feed so the restored item reappears.
      void queryClient.invalidateQueries({ queryKey: ["posts"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
      void queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  const purge = useMutation({
    mutationFn: ({ kind, id }: { kind: string; id: string }) =>
      apiFetch(`/families/${familySlug}/trash/${kind}/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trash", familySlug] });
    },
  });

  if (trashQuery.isLoading) return <p className="text-muted">Loading…</p>;
  if (trashQuery.error)
    return <p className="text-error">{(trashQuery.error as Error).message}</p>;

  const items = trashQuery.data?.items ?? [];
  if (items.length === 0) {
    return <p className="text-muted">Nothing in the trash.</p>;
  }

  return (
    <ul className="list-unstyled stack">
      {items.map((item) => (
        <li
          key={`${item.kind}-${item.id}`}
          className="card"
          style={{ display: "flex", flexDirection: "column", gap: "var(--s-2)" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "var(--s-3)",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="badge">{KIND_LABEL[item.kind] ?? item.kind}</span>
                <strong style={{ wordBreak: "break-word" }}>
                  {item.title || <em className="text-subtle">(no title)</em>}
                </strong>
              </div>
              <div className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                {item.authorName ? `by ${item.authorName}` : "unknown author"}
                {" · created "}
                {new Date(item.createdAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
              </div>
              <div className="text-subtle" style={{ fontSize: "0.8rem" }}>
                deleted {new Date(item.deletedAt).toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                {item.deletedByName ? ` by ${item.deletedByName}` : ""}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => restore.mutate({ kind: item.kind, id: item.id })}
                disabled={restore.isPending}
                style={{ minHeight: 30, padding: "4px 12px", fontSize: "0.8rem" }}
              >
                Restore
              </button>
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => {
                  if (
                    window.confirm(
                      `Permanently delete this ${item.kind}? This cannot be undone.`,
                    )
                  ) {
                    purge.mutate({ kind: item.kind, id: item.id });
                  }
                }}
                disabled={purge.isPending}
                style={{
                  minHeight: 30,
                  padding: "4px 12px",
                  fontSize: "0.8rem",
                  color: "var(--c-danger)",
                }}
              >
                Delete forever
              </button>
            </div>
          </div>
        </li>
      ))}
      {(restore.error || purge.error) && (
        <p className="text-error">
          {(restore.error ?? purge.error)?.message}
        </p>
      )}
    </ul>
  );
}
